import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DatePicker } from "@/components/ui/date-picker";
import { Textarea } from "@/components/ui/textarea";
import { Field, FieldLabel } from "@/components/ui/field";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { AlertTriangle, Check, ChevronDown, ChevronsUpDown, Info, Search } from "lucide-react";
import { useNotification } from "@/shared/hooks/useNotification";
import { useConfirm } from "@/shared/contexts/confirm-context";
import { SowingConfirmBody } from "./SowingConfirmSummary";
import { useActiveCropTypes } from "../../hooks/useCropTypes";
import { useVarietyPricingByCropType } from "../../hooks/useVarietyPricings";
import { useOrdersReadyForSowing } from "../../hooks/useOrders";
import { useGreenhouses, useGreenhouseBeds, useMultiGreenhouseBeds } from "@/features/farms/hooks/useGreenhouses";
import {  useMultiGreenhouseBays } from "@/features/farms/hooks/useGreenhouseBays";
import {
  useSalesCreateDraft,
  useSubmitSowingInstruction,
  useAcceptSowingInstruction,
  useAcknowledgeReceipt,
  useGerminationSplitPreview,
  useRecordGermination,
} from "../../hooks/useSowingInstructions";
import { useRejectInstruction, useRecordGerminationWave, useSowingsByInstruction, useCloseForSowing } from "../../hooks/useSowingLifecycle";
import {  useSeedBatchesByCustomer } from "../../hooks/useSeedBatches";
import type { SowingInstructionDto } from "../../services/sowingInstructionsApi";
import type { SowingDto } from "../../services/sowingLifecycleApi";
import { getErrorMessage } from "@/shared/utils/errorHandler";

// ─── Sales Create Draft Dialog ───────────────────────────────────────────────

interface SalesCreateDraftDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
  orderId?: string;
  orderItemId?: string;
}

const TRAY_TYPE_OPTIONS = [
  { value: "210", label: "210-hole" },
  { value: "442", label: "442-hole" },
];

const PURPOSE_OPTIONS = [
  { value: "1", label: "Customer Order" },
  { value: "2", label: "Company Stock" },
];

const SEED_SOURCE_OPTIONS = [
  { value: "1", label: "Company Inventory" },
  { value: "2", label: "Customer Provided" },
];

// UnitOfMeasure enum: Trays = 11, Pieces = 5
const UNIT_TRAYS = 11;
const UNIT_PIECES = 5;

const getUnitLabel = (unit?: number) => {
  if (unit === UNIT_PIECES) {
    return "pieces";
  }
  if (unit === UNIT_TRAYS) {
    return "trays";
  }
  return "units";
};

export const SalesCreateDraftDialog = ({
  open,
  onOpenChange,
  onSuccess,
  orderId,
  orderItemId,
}: SalesCreateDraftDialogProps) => {
  const notify = useNotification();
  const confirm = useConfirm();
  const cropTypesQuery = useActiveCropTypes();
  const createMutation = useSalesCreateDraft();
  const submitMutation = useSubmitSowingInstruction();

  const [cropTypeOpen, setCropTypeOpen] = useState(false);
  const [varietyOpen, setVarietyOpen] = useState(false);
  const [customerOrderOpen, setCustomerOrderOpen] = useState(false);
  const [seedBatchOpen, setSeedBatchOpen] = useState(false);
  const [selectedCustomerOrderId, setSelectedCustomerOrderId] = useState(orderId || "");
  const [selectedCustomerOrderItemId, setSelectedCustomerOrderItemId] = useState(orderItemId || "");
  const [selectedCustomerId, setSelectedCustomerId] = useState("");
  const today = new Date().toISOString().split("T")[0];
  const [form, setForm] = useState({
    cropTypeId: "",
    variety: "",
    varietyPricingId: "",
    trayType: "210" as string,
    pricingMode: 1 as number,
    quantityToSow: "",
    purpose: orderId ? "1" : "2",
    seedSource: "1",
    seedBatchId: "",
    scheduledStartDate: today,
    scheduledEndDate: today,
    notes: "",
  });

  const varietyPricingsQuery = useVarietyPricingByCropType(form.cropTypeId);
  const ordersReadyQuery = useOrdersReadyForSowing();

  // Flatten pending customer-seed order items from orders ready for sowing
  const pendingCustomerOrderItems = (ordersReadyQuery.data ?? []).flatMap((order) =>
    (order.orderItems ?? [])
      .filter((item) => !item.isCompanySeedling && item.quantityPending > 0)
      .map((item) => ({
        ...item,
        orderNumber: order.orderNumber,
        customerName: order.customerName,
        customerId: order.customerId,
        orderId: order.id,
        orderItemId: item.id,
      }))
  );

  // Fetch seed batches by customer when seedSource = CustomerProvided
  const effectiveOrderId = orderId || selectedCustomerOrderId;
  const effectiveOrderItemId = orderItemId || selectedCustomerOrderItemId;
  // Resolve customer ID: from dropdown selection, or from the orderId prop via orders data
  const effectiveCustomerId = selectedCustomerId
    || (orderId ? ordersReadyQuery.data?.find((o) => o.id === orderId)?.customerId : undefined)
    || "";
  const seedBatchesQuery = useSeedBatchesByCustomer(
    form.seedSource === "2" && effectiveCustomerId ? effectiveCustomerId : undefined
  );

  const handleSubmit = (andSubmit: boolean) => {
    if (!form.cropTypeId || !form.variety || !form.quantityToSow) {
      notify.error("Crop type, variety and quantity are required");
      return;
    }
    if (form.seedSource === "2" && !form.seedBatchId) {
      notify.error("Please select the customer seed batch");
      return;
    }
    if (!form.scheduledStartDate || !form.scheduledEndDate) {
      notify.error("Sowing window start and end dates are required");
      return;
    }
    if (form.scheduledEndDate < form.scheduledStartDate) {
      notify.error("Sowing window end date must be on or after the start date");
      return;
    }
    const isPerSeedling = form.pricingMode === 2;
    const payload = {
      cropTypeId: form.cropTypeId,
      varietyPricingId: form.varietyPricingId || undefined,
      variety: form.variety,
      ...(isPerSeedling ? {} : { trayType: parseInt(form.trayType) as 210 | 442 }),
      pricingMode: form.pricingMode,
      quantityToSow: parseFloat(form.quantityToSow),
      unit: isPerSeedling ? UNIT_PIECES : UNIT_TRAYS,
      purpose: parseInt(form.purpose),
      seedSource: parseInt(form.seedSource),
      seedBatchId: form.seedSource === "2" ? form.seedBatchId : undefined,
      scheduledStartDate: form.scheduledStartDate,
      scheduledEndDate: form.scheduledEndDate,
      orderId: effectiveOrderId || undefined,
      orderItemId: effectiveOrderItemId || undefined,
      notes: form.notes || undefined,
    };

    confirm({
      title: andSubmit ? "Submit sowing instruction?" : "Save draft?",
      description: andSubmit
        ? `${form.variety} — ${form.quantityToSow} will be submitted to the Sowing Supervisor.`
        : `${form.variety} — ${form.quantityToSow} will be saved as a draft.`,
      confirmLabel: andSubmit ? "Submit" : "Save draft",
      onConfirm: async () => {
        let draft;
        try {
          draft = await createMutation.mutateAsync(payload);
        } catch (err: unknown) {
          notify.error(getErrorMessage(err) || "Failed to create");
          throw err;
        }

        if (!andSubmit) {
          notify.success("Draft saved — submit it when ready");
          onOpenChange(false);
          onSuccess();
          return;
        }

        // Chain: draft created → now submit it to Pending
        try {
          await submitMutation.mutateAsync({
            id: draft.id,
            payload: { sowingInstructionId: draft.id, notes: form.notes },
          });
          notify.success("Sowing instruction submitted to Sowing Supervisor");
          onOpenChange(false);
          onSuccess();
        } catch (err: unknown) {
          notify.error(getErrorMessage(err) || "Created but failed to submit — find it in drafts");
          throw err;
        }
      },
    });
  };

  const isPending = createMutation.isPending || submitMutation.isPending;
  const hasNoPricing =
    !!form.cropTypeId
    && !varietyPricingsQuery.isLoading
    && (varietyPricingsQuery.data?.length ?? 0) === 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create Sowing Instruction</DialogTitle>
          <DialogDescription>
            Fill in what to sow. The Sowing Supervisor will fill in operational details.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 [&>*]:min-w-0">
            <Field>
              <FieldLabel>Crop Type *</FieldLabel>
              <Popover open={cropTypeOpen} onOpenChange={setCropTypeOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={cropTypeOpen}
                    className="w-full justify-between font-normal"
                  >
                    {form.cropTypeId
                      ? cropTypesQuery.data?.find((ct) => ct.id === form.cropTypeId)?.name ?? "Select crop type"
                      : "Select crop type"}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Search crop type..." />
                    <CommandList>
                      <CommandEmpty>No crop types found.</CommandEmpty>
                      <CommandGroup>
                        {cropTypesQuery.data?.map((ct) => (
                          <CommandItem
                            key={ct.id}
                            value={ct.name}
                            onSelect={() => {
                              setForm((f) => ({ ...f, cropTypeId: ct.id, variety: "", varietyPricingId: "" }));
                              setCropTypeOpen(false);
                            }}
                          >
                            <Check
                              className={cn(
                                "mr-2 h-4 w-4",
                                form.cropTypeId === ct.id ? "opacity-100" : "opacity-0"
                              )}
                            />
                            {ct.name}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </Field>
            <Field>
              <FieldLabel>Variety *</FieldLabel>
              {form.cropTypeId ? (
                <Popover open={varietyOpen} onOpenChange={setVarietyOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={varietyOpen}
                      className="w-full justify-between font-normal h-auto py-2"
                    >
                      {form.varietyPricingId
                        ? (() => {
                            const vp = varietyPricingsQuery.data?.find((v) => v.id === form.varietyPricingId);
                            if (!vp) return "Select variety & pricing";
                            const name = vp.varietyName || vp.cropTypeName;
                            const priceLabel = vp.companySeedlingPrice != null
                              ? `KES ${vp.companySeedlingPrice}`
                              : (vp.propagationServicePrice != null ? `KES ${vp.propagationServicePrice} (propagation)` : "no price");
                            const pricing = vp.pricingMode === 2
                              ? `Per Seedling/Piece — ${priceLabel}`
                              : `${vp.trayTypeName || vp.trayType + '-Hole'} — ${priceLabel}`;
                            return (
                              <span className="flex flex-col items-start text-left min-w-0 flex-1">
                                <span className="truncate w-full">{name}</span>
                                <span className="truncate w-full text-xs text-muted-foreground">{pricing}</span>
                              </span>
                            );
                          })()
                        : "Select variety & pricing"}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                    <Command>
                      <CommandInput placeholder="Search variety & pricing..." />
                      <CommandList>
                        <CommandEmpty>No varieties found.</CommandEmpty>
                        <CommandGroup>
                          {varietyPricingsQuery.data?.map((vp) => {
                            const name = vp.varietyName || vp.cropTypeName;
                            const priceLabel = vp.companySeedlingPrice != null
                              ? `KES ${vp.companySeedlingPrice}`
                              : (vp.propagationServicePrice != null ? `KES ${vp.propagationServicePrice} (propagation)` : "no price");
                            const pricing = vp.pricingMode === 2
                              ? `Per Seedling/Piece — ${priceLabel}`
                              : `${vp.trayTypeName || vp.trayType + '-Hole'} — ${priceLabel}`;
                            return (
                              <CommandItem
                                key={vp.id}
                                value={`${name} ${pricing}`}
                                onSelect={() => {
                                  setForm((f) => ({
                                    ...f,
                                    varietyPricingId: vp.id,
                                    variety: vp.varietyName || "",
                                    pricingMode: vp.pricingMode ?? 1,
                                    trayType: vp.trayType ? String(vp.trayType) : f.trayType,
                                  }));
                                  setVarietyOpen(false);
                                }}
                              >
                                <Check
                                  className={cn(
                                    "mr-2 h-4 w-4 shrink-0",
                                    form.varietyPricingId === vp.id ? "opacity-100" : "opacity-0"
                                  )}
                                />
                                <div className="flex flex-col min-w-0 flex-1">
                                  <span className="truncate">{name}</span>
                                  <span className="truncate text-xs text-muted-foreground">{pricing}</span>
                                </div>
                              </CommandItem>
                            );
                          })}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              ) : (
                <Input
                  value={form.variety}
                  onChange={(e) => setForm((f) => ({ ...f, variety: e.target.value }))}
                  placeholder="Select crop type first"
                  disabled
                />
              )}
              {form.cropTypeId
                && !varietyPricingsQuery.isLoading
                && (varietyPricingsQuery.data?.length ?? 0) === 0 && (
                <Alert variant="destructive" className="mt-2">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>
                    No varieties with pricing have been configured for this crop type yet.
                    Ask an administrator to add a Variety Pricing entry before creating a sowing instruction.
                  </AlertDescription>
                </Alert>
              )}
            </Field>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 [&>*]:min-w-0">
            {form.pricingMode !== 2 && (
              <Field>
                <FieldLabel>Tray Type *</FieldLabel>
                <Select value={form.trayType} onValueChange={(v) => setForm((f) => ({ ...f, trayType: v }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TRAY_TYPE_OPTIONS.map((t) => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            )}
            <Field>
              <FieldLabel>{form.pricingMode === 2 ? "Quantity (Seedlings/Pieces) *" : "Quantity (Trays) *"}</FieldLabel>
              <Input
                type="number"
                value={form.quantityToSow}
                onChange={(e) => setForm((f) => ({ ...f, quantityToSow: e.target.value }))}
                placeholder="0"
              />
            </Field>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 [&>*]:min-w-0">
            <Field>
              <FieldLabel>Purpose</FieldLabel>
              <Select value={form.purpose} onValueChange={(v) => setForm((f) => ({ ...f, purpose: v }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PURPOSE_OPTIONS.map((p) => (
                    <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field>
              <FieldLabel>Seed Source</FieldLabel>
              <Select value={form.seedSource} onValueChange={(v) => setForm((f) => ({ ...f, seedSource: v, seedBatchId: "" }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SEED_SOURCE_OPTIONS.map((s) => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </div>

          {/* Pending customer order picker — shown when purpose is Customer Order and no order already linked */}
          {form.purpose === "1" && !orderId && pendingCustomerOrderItems.length > 0 && (
            <Field>
              <FieldLabel>Link to Pending Customer Order</FieldLabel>
              <Popover open={customerOrderOpen} onOpenChange={setCustomerOrderOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={customerOrderOpen}
                    className="w-full justify-between font-normal h-auto py-2"
                    disabled={isPending}
                  >
                    {selectedCustomerOrderItemId
                      ? (() => {
                          const sel = pendingCustomerOrderItems.find(i => i.id === selectedCustomerOrderItemId);
                          return sel
                            ? `${sel.customerName} — ${sel.variety || sel.productName} (${sel.quantityPending} pending)`
                            : "Select customer order";
                        })()
                      : "Select pending customer order"}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Search by customer, variety..." />
                    <CommandList>
                      <CommandEmpty>No pending customer seed orders found.</CommandEmpty>
                      <CommandGroup>
                        {pendingCustomerOrderItems.map((item) => (
                          <CommandItem
                            key={item.id}
                            value={`${item.customerName} ${item.orderNumber} ${item.variety || ""} ${item.cropType || ""}`}
                            onSelect={() => {
                              // Auto-fill form from the selected order item
                              const cropType = cropTypesQuery.data?.find(
                                (ct) => ct.name === item.cropType
                              );
                              const vp = cropType
                                ? varietyPricingsQuery.data?.find(
                                    (v) => v.varietyName === item.variety
                                  )
                                : undefined;

                              setForm((f) => ({
                                ...f,
                                cropTypeId: cropType?.id || f.cropTypeId,
                                variety: item.variety || f.variety,
                                varietyPricingId: vp?.id || f.varietyPricingId,
                                pricingMode: vp?.pricingMode ?? item.pricingMode ?? f.pricingMode,
                                trayType: item.trayType ? String(item.trayType) : f.trayType,
                                quantityToSow: item.quantityPending.toString(),
                                seedSource: "2",
                              }));
                              setSelectedCustomerOrderId(item.orderId);
                              setSelectedCustomerOrderItemId(item.id);
                              setSelectedCustomerId(item.customerId);
                              setCustomerOrderOpen(false);
                            }}
                          >
                            <Check
                              className={cn(
                                "mr-2 h-4 w-4 shrink-0",
                                selectedCustomerOrderItemId === item.id ? "opacity-100" : "opacity-0"
                              )}
                            />
                            <div className="flex flex-col min-w-0 flex-1">
                              <span className="truncate font-medium">{item.customerName} — {item.orderNumber}</span>
                              <span className="truncate text-xs text-muted-foreground">
                                {item.variety || item.productName} ({item.cropType || "—"}) · {item.quantityPending} pending
                              </span>
                            </div>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </Field>
          )}

          {/* Seed batch picker — only when Customer Provided and a customer is known */}
          {form.seedSource === "2" && (
            <Field>
              <FieldLabel>Customer Seed Batch *</FieldLabel>
              {!effectiveCustomerId ? (
                <Alert>
                  <Info className="h-4 w-4" />
                  <AlertDescription>
                    Customer seed batches are linked to customers. Select a pending customer order above, or open this form from a customer order.
                  </AlertDescription>
                </Alert>
              ) : seedBatchesQuery.isLoading ? (
                <p className="text-sm text-muted-foreground">Loading seed batches…</p>
              ) : (() => {
                // Filter seed batches by crop type / variety matching the form selection
                const filteredBatches = (seedBatchesQuery.data ?? []).filter((b) => {
                  if (form.cropTypeId && b.cropTypeId && b.cropTypeId !== form.cropTypeId) return false;
                  if (form.variety && b.variety && b.variety.toLowerCase() !== form.variety.toLowerCase()) return false;
                  return true;
                });

                if (!filteredBatches.length) {
                  return (
                    <Alert>
                      <AlertTriangle className="h-4 w-4" />
                      <AlertDescription>
                        No seed batches found for this customer{form.variety ? ` matching "${form.variety}"` : ""}. The Store Keeper must enter the customer's seeds first.
                      </AlertDescription>
                    </Alert>
                  );
                }

                const selectedBatch = filteredBatches.find((b) => b.id === form.seedBatchId);

                return (
                  <Popover open={seedBatchOpen} onOpenChange={setSeedBatchOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        role="combobox"
                        aria-expanded={seedBatchOpen}
                        className="w-full justify-between font-normal h-auto py-2"
                      >
                        {selectedBatch
                          ? `${selectedBatch.batchNumber} — ${selectedBatch.variety ?? "Unknown"} (${selectedBatch.quantityAvailable} available)${String(selectedBatch.approvalStatus) !== "Approved" && selectedBatch.approvalStatus !== 2 ? " ⚠ Not approved" : ""}`
                          : "Select seed batch"}
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                      <Command>
                        <CommandInput placeholder="Search batch number, variety..." />
                        <CommandList>
                          <CommandEmpty>No matching seed batches.</CommandEmpty>
                          <CommandGroup>
                            {filteredBatches.map((b) => (
                              <CommandItem
                                key={b.id}
                                value={`${b.batchNumber} ${b.variety ?? ""} ${b.cropTypeName ?? ""}`}
                                onSelect={() => {
                                  setForm((f) => ({ ...f, seedBatchId: b.id }));
                                  setSeedBatchOpen(false);
                                }}
                              >
                                <Check
                                  className={cn(
                                    "mr-2 h-4 w-4 shrink-0",
                                    form.seedBatchId === b.id ? "opacity-100" : "opacity-0"
                                  )}
                                />
                                <div className="flex flex-col min-w-0 flex-1">
                                  <span className="truncate font-medium">
                                    {b.batchNumber} — {b.variety ?? "Unknown variety"}
                                  </span>
                                  <span className="truncate text-xs text-muted-foreground">
                                    {b.cropTypeName ?? ""} · {b.quantityAvailable} available
                                    {String(b.approvalStatus) !== "Approved" && b.approvalStatus !== 2 && " · ⚠ Not approved"}
                                  </span>
                                </div>
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                );
              })()}
            </Field>
          )}

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 [&>*]:min-w-0">
            <Field>
              <FieldLabel>Sowing Window Start *</FieldLabel>
              <DatePicker
                value={form.scheduledStartDate}
                onChange={(value) => setForm((f) => ({
                  ...f,
                  scheduledStartDate: value,
                  // Keep end >= start by bumping end forward when start crosses it
                  scheduledEndDate: f.scheduledEndDate && f.scheduledEndDate < value ? value : f.scheduledEndDate,
                }))}
              />
            </Field>
            <Field>
              <FieldLabel>Sowing Window End *</FieldLabel>
              <DatePicker
                min={form.scheduledStartDate || undefined}
                value={form.scheduledEndDate}
                onChange={(value) => setForm((f) => ({ ...f, scheduledEndDate: value }))}
              />
            </Field>
          </div>
          <p className="text-xs text-muted-foreground -mt-2">
            Pick the window during which sowing can occur. Germination date is auto-calculated from the crop lifecycle starting at the window start.
          </p>

          <Field>
            <FieldLabel>Notes</FieldLabel>
            <Textarea
              value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              placeholder="Optional notes for the Sowing Supervisor"
              rows={2}
            />
          </Field>

          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button
              onClick={() => handleSubmit(false)}
              disabled={isPending || hasNoPricing}
              variant="outline"
            >
              Save Draft
            </Button>
            <Button
              onClick={() => handleSubmit(true)}
              disabled={isPending || hasNoPricing}
            >
              {isPending ? "Saving..." : "Create & Submit"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ─── Accept SI Dialog (Sowing Supervisor) ────────────────────────────────────

interface AcceptSIDialogProps {
  instruction: SowingInstructionDto;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export const AcceptSIDialog = ({ instruction, open, onOpenChange, onSuccess }: AcceptSIDialogProps) => {
  const notify = useNotification();
  const acceptMutation = useAcceptSowingInstruction();
  const rejectMutation = useRejectInstruction();
  const confirm = useConfirm();

  const [mode, setMode] = useState<"accept" | "reject">("accept");
  const [specialInstructions, setSpecialInstructions] = useState("");
  const [notes, setNotes] = useState("");
  const [rejectReason, setRejectReason] = useState("");

  const close = () => {
    onOpenChange(false);
    setMode("accept");
    setSpecialInstructions("");
    setNotes("");
    setRejectReason("");
  };

  const handleAccept = () => {
    confirm({
      title: "Accept & issue sowing instruction?",
      description: "This instruction will be accepted and issued for sowing.",
      confirmLabel: "Accept & issue",
      body: SowingConfirmBody(instruction, [
        { label: "Quantity to sow", value: `${instruction.quantityToSow}` },
      ]),
      onConfirm: () =>
        acceptMutation
          .mutateAsync({
            id: instruction.id,
            payload: {
              sowingInstructionId: instruction.id,
              specialInstructions: specialInstructions || undefined,
              notes: notes || undefined,
            },
          })
          .then(() => {
            notify.success("Sowing instruction accepted");
            close();
            onSuccess();
          })
          .catch((err: unknown) => {
            notify.error(getErrorMessage(err) || "Failed to accept");
            throw err;
          }),
    });
  };

  const handleReject = () => {
    if (!rejectReason.trim()) {
      notify.error("Please provide a reason for rejection");
      return;
    }
    rejectMutation.mutate(
      { sowingInstructionId: instruction.id, reason: rejectReason.trim() },
      {
        onSuccess: () => {
          notify.success("Sowing instruction rejected");
          close();
          onSuccess();
        },
        onError: (err: unknown) =>
          notify.error(getErrorMessage(err) || "Failed to reject"),
      }
    );
  };

  const isPending = acceptMutation.isPending || rejectMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? onOpenChange(true) : close())}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {mode === "reject" ? "Reject Sowing Instruction" : "Accept Sowing Instruction"}
          </DialogTitle>
          <DialogDescription>
            {instruction.instructionNumber} — {instruction.cropTypeName} {instruction.variety} ({instruction.quantityToSow})
          </DialogDescription>
        </DialogHeader>

        {mode === "accept" ? (
          <div className="space-y-4">
            <Field>
              <FieldLabel>Special Instructions</FieldLabel>
              <Textarea
                value={specialInstructions}
                onChange={(e) => setSpecialInstructions(e.target.value)}
                rows={2}
              />
            </Field>

            <Field>
              <FieldLabel>Notes</FieldLabel>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
              />
            </Field>

            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={close} disabled={isPending}>Cancel</Button>
              <Button
                variant="destructive"
                onClick={() => setMode("reject")}
                disabled={isPending}
              >
                Reject…
              </Button>
              <Button onClick={handleAccept} disabled={isPending}>
                {acceptMutation.isPending ? "Accepting..." : "Accept & Issue"}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <Field>
              <FieldLabel>Reason for rejection *</FieldLabel>
              <Textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Explain why this instruction is being rejected"
                rows={4}
                autoFocus
              />
            </Field>

            <div className="flex gap-2 justify-end">
              <Button
                variant="outline"
                onClick={() => setMode("accept")}
                disabled={isPending}
              >
                Back
              </Button>
              <Button
                variant="destructive"
                onClick={handleReject}
                disabled={isPending || !rejectReason.trim()}
              >
                {rejectMutation.isPending ? "Rejecting..." : "Confirm Reject"}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// ─── Acknowledge Receipt Dialog (Growing Supervisor) ─────────────────────────

interface AcknowledgeReceiptDialogProps {
  instruction: SowingInstructionDto;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export const AcknowledgeReceiptDialog = ({
  instruction,
  open,
  onOpenChange,
  onSuccess,
}: AcknowledgeReceiptDialogProps) => {
  const notify = useNotification();
  const acknowledgeMutation = useAcknowledgeReceipt();
  const confirm = useConfirm();
  const greenhousesQuery = useGreenhouses();
  const [selectedGreenhouse, setSelectedGreenhouse] = useState("");
  const [selectedBays, setSelectedBays] = useState<string[]>([]);
  const [bedAllocations, setBedAllocations] = useState<Map<string, number>>(new Map());

  const bedsQuery = useGreenhouseBeds(selectedGreenhouse);
  const allBeds = bedsQuery.data ?? [];

  // Derive unique bays from beds
  const bays = allBeds.reduce<{ id: string; name: string }[]>((acc, bed) => {
    if (!acc.find((b) => b.id === bed.bayId)) {
      acc.push({ id: bed.bayId, name: bed.bayName });
    }
    return acc;
  }, []);

  // Filter beds by selected bays (if any)
  const filteredBeds = selectedBays.length > 0
    ? allBeds.filter((b) => selectedBays.includes(b.bayId))
    : allBeds;

  const toggleBay = (id: string) =>
    setSelectedBays((prev) => {
      const next = prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
      // Drop bed allocations that are no longer in scope
      setBedAllocations((current) => {
        const updated = new Map(current);
        for (const bedId of Array.from(updated.keys())) {
          const bed = allBeds.find((b) => b.id === bedId);
          if (next.length > 0 && (!bed || !next.includes(bed.bayId))) {
            updated.delete(bedId);
          }
        }
        return updated;
      });
      return next;
    });

  const toggleBed = (id: string) =>
    setBedAllocations((prev) => {
      const next = new Map(prev);
      if (next.has(id)) next.delete(id);
      else next.set(id, 0);
      return next;
    });

  const setBedTrayCount = (id: string, value: string) => {
    const n = Math.max(0, parseInt(value || "0", 10) || 0);
    setBedAllocations((prev) => {
      const next = new Map(prev);
      next.set(id, n);
      return next;
    });
  };

  const allocatedTotal = Array.from(bedAllocations.values()).reduce((a, b) => a + b, 0);

  const unitLabel = getUnitLabel(instruction?.unit);
  // qtyGrowingNum is computed below; declarations below depend on it for validity flags.
  const germinatedQty = instruction.quantityGerminated ?? instruction.quantityToSow;
  const stockQty = instruction.stockQuantity ?? 0;
  const fillingReserve = instruction.fillingReserve ?? 0;

  const [form, setForm] = useState({
    quantityGrowing: String(germinatedQty),
    expectedReadyDate: "",
    notes: "",
  });

  const qtyGrowingNum = parseFloat(form.quantityGrowing) || 0;
  const growingLoss = Math.max(0, germinatedQty - qtyGrowingNum);

  const hasZeroAllocations = Array.from(bedAllocations.values()).some((c) => c <= 0);
  const allocationsValid =
    bedAllocations.size === 0 ||
    (!hasZeroAllocations && allocatedTotal === Math.floor(qtyGrowingNum));
  const allocationDiff = allocatedTotal - Math.floor(qtyGrowingNum);

  const handleSubmit = () => {
    if (!selectedGreenhouse) {
      notify.error("Greenhouse is required");
      return;
    }
    if (!form.quantityGrowing || qtyGrowingNum <= 0) {
      notify.error("Please enter the quantity actually entering the growing bed");
      return;
    }
    if (qtyGrowingNum > germinatedQty) {
      notify.error(`Quantity growing cannot exceed germinated quantity (${germinatedQty} ${unitLabel})`);
      return;
    }
    if (bedAllocations.size > 0) {
      if (Array.from(bedAllocations.values()).some((c) => c <= 0)) {
        notify.error("Each selected bed must have a tray count greater than zero");
        return;
      }
      if (allocatedTotal !== Math.floor(qtyGrowingNum)) {
        notify.error(`Per-bed trays must total ${Math.floor(qtyGrowingNum)} (currently ${allocatedTotal})`);
        return;
      }
    }
    confirm({
      title: "Acknowledge receipt?",
      description: "A greenhouse stock entry will be created for this instruction.",
      confirmLabel: "Acknowledge & create stock entry",
      body: SowingConfirmBody(instruction, [
        { label: "Quantity growing", value: `${qtyGrowingNum}` },
        { label: "Beds", value: `${bedAllocations.size}` },
      ]),
      onConfirm: () =>
        acknowledgeMutation
          .mutateAsync({
            id: instruction.id,
            payload: {
              sowingInstructionId: instruction.id,
              greenhouseId: selectedGreenhouse,
              bedAllocations: Array.from(bedAllocations.entries()).map(([bedId, trayCount]) => ({ bedId, trayCount })),
              quantityGrowing: qtyGrowingNum,
              expectedReadyDate: form.expectedReadyDate
                ? new Date(form.expectedReadyDate).toISOString()
                : undefined,
              notes: form.notes,
            },
          })
          .then(() => {
            notify.success("Receipt acknowledged — greenhouse stock entry created");
            onOpenChange(false);
            onSuccess();
          })
          .catch((err: unknown) => {
            notify.error(getErrorMessage(err) || "Failed to acknowledge");
            throw err;
          }),
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Acknowledge Receipt — Growing Supervisor</DialogTitle>
          <DialogDescription>
            {instruction.instructionNumber} — {instruction.cropTypeName} {instruction.variety}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Germination summary */}
          {(stockQty > 0 || fillingReserve > 0 || germinatedQty > 0) && (
            <div className="rounded-md border bg-muted/40 p-3 space-y-2 text-sm">
              <p className="font-medium text-muted-foreground uppercase text-[10px] tracking-wide">Germination summary</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                <div>
                  <p className="text-muted-foreground">Germinated</p>
                  <p className="font-semibold">{germinatedQty} {unitLabel}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">To stock (70%)</p>
                  <p className="font-semibold text-green-700 dark:text-green-400">{stockQty} {unitLabel}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Reserve (30%)</p>
                  <p className="font-semibold text-amber-700 dark:text-amber-400">{fillingReserve} {unitLabel}</p>
                </div>
              </div>
            </div>
          )}

          {/* Quantity actually entering growing — key new field */}
          <div className="rounded-md border border-blue-200 bg-blue-50 dark:bg-blue-950/20 p-3 space-y-2">
            <p className="text-sm font-semibold">How many are going into the growing bed?</p>
            <p className="text-xs text-muted-foreground">
              Enter the actual count being transferred. This may be less than germinated if any
              were lost to disease, transplant shock, or physical damage during handling.
            </p>
            <div className="flex items-end gap-3">
              <div className="flex-1">
                <label className="text-xs font-medium text-foreground block mb-1">
                  Quantity entering growing <span className="text-destructive">*</span>
                </label>
                <Input
                  type="number"
                  step="0.01"
                  value={form.quantityGrowing}
                  onChange={(e) => setForm((f) => ({ ...f, quantityGrowing: e.target.value }))}
                  placeholder={`e.g. ${germinatedQty}`}
                  disabled={acknowledgeMutation.isPending}
                />
              </div>
              <div className="text-right min-w-[90px]">
                <p className="text-xs text-muted-foreground">Transfer loss</p>
                <p className={`text-sm font-semibold ${growingLoss > 0 ? "text-red-600" : "text-muted-foreground"}`}>
                  {growingLoss > 0 ? `−${growingLoss} ${unitLabel}` : "None"}
                </p>
              </div>
            </div>
          </div>

          <Field>
            <FieldLabel>Greenhouse *</FieldLabel>
            <Select
              value={selectedGreenhouse}
              onValueChange={(v) => {
                setSelectedGreenhouse(v);
                setSelectedBays([]);
                setBedAllocations(new Map());
              }}
            >
              <SelectTrigger><SelectValue placeholder="Select greenhouse" /></SelectTrigger>
              <SelectContent>
                {(greenhousesQuery.data ?? []).map((g) => (
                  <SelectItem key={g.id} value={g.id}>{g.greenhouseName}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          <Field>
            <FieldLabel>Bays (optional filter)</FieldLabel>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  className="w-full justify-between font-normal"
                  disabled={!selectedGreenhouse || bays.length === 0}
                >
                  <span className="truncate">
                    {!selectedGreenhouse
                      ? "Select greenhouse first"
                      : bays.length === 0
                      ? "No bays configured"
                      : selectedBays.length === 0
                      ? "All bays"
                      : `${selectedBays.length} bay${selectedBays.length === 1 ? "" : "s"} selected`}
                  </span>
                  <ChevronDown className="h-4 w-4 opacity-60" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[--radix-popover-trigger-width] p-2" align="start">
                <div className="max-h-60 overflow-y-auto space-y-1">
                  {bays.map((b) => (
                    <label
                      key={b.id}
                      className="flex items-center gap-2 rounded px-2 py-1.5 hover:bg-muted cursor-pointer text-sm"
                    >
                      <Checkbox
                        checked={selectedBays.includes(b.id)}
                        onCheckedChange={() => toggleBay(b.id)}
                      />
                      <span>{b.name}</span>
                    </label>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
          </Field>

          <Field>
            <FieldLabel>Beds</FieldLabel>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  className="w-full justify-between font-normal"
                  disabled={!selectedGreenhouse || filteredBeds.length === 0}
                >
                  <span className="truncate">
                    {!selectedGreenhouse
                      ? "Select greenhouse first"
                      : filteredBeds.length === 0
                      ? selectedBays.length > 0
                        ? "No beds in selected bays"
                        : "No beds configured for this greenhouse"
                      : bedAllocations.size === 0
                      ? "Assign trays to beds"
                      : `${bedAllocations.size} bed${bedAllocations.size === 1 ? "" : "s"} — ${allocatedTotal}/${Math.floor(qtyGrowingNum) || 0}`}
                  </span>
                  <ChevronDown className="h-4 w-4 opacity-60" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[--radix-popover-trigger-width] p-2" align="start">
                <div className="max-h-60 overflow-y-auto space-y-1">
                  {filteredBeds.map((b) => {
                    const isSelected = bedAllocations.has(b.id);
                    const allocation = bedAllocations.get(b.id) ?? 0;
                    return (
                      <div
                        key={b.id}
                        className="flex items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-muted"
                      >
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={() => toggleBed(b.id)}
                        />
                        <span className="flex-1">
                          {b.bayName} — Bed {b.bedNumber}
                          {b.currentOccupancy > 0 ? ` (${b.currentOccupancy} trays on bed)` : ""}
                        </span>
                        {isSelected && (
                          <Input
                            type="number"
                            min="1"
                            step="1"
                            placeholder="trays"
                            value={allocation || ""}
                            onChange={(e) => setBedTrayCount(b.id, e.target.value)}
                            className="h-7 w-20 text-sm"
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              </PopoverContent>
            </Popover>
            {bedAllocations.size > 0 && (
              <p className={`mt-1 text-xs break-words ${allocationsValid ? "text-muted-foreground" : "text-destructive"}`}>
                Allocated {allocatedTotal} / {Math.floor(qtyGrowingNum) || 0} trays
                {allocationDiff > 0 && ` — over by ${allocationDiff}, reduce a bed's count`}
                {allocationDiff < 0 && ` — need ${-allocationDiff} more tray${-allocationDiff === 1 ? "" : "s"}`}
              </p>
            )}
            {selectedGreenhouse && allBeds.length === 0 && !bedsQuery.isLoading && (
              <p className="text-xs text-muted-foreground mt-1">
                No beds configured for this greenhouse — add some in Farms before acknowledging.
              </p>
            )}
          </Field>

          <Field>
            <FieldLabel>Expected Ready Date</FieldLabel>
            <DatePicker
              value={form.expectedReadyDate}
              onChange={(v) => setForm((f) => ({ ...f, expectedReadyDate: v }))}
            />
          </Field>

          <Field>
            <FieldLabel>Notes</FieldLabel>
            <Textarea
              value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              rows={2}
            />
          </Field>

          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={acknowledgeMutation.isPending || !allocationsValid}>
              {acknowledgeMutation.isPending ? "Acknowledging..." : "Acknowledge & Create Stock Entry"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ─── Record Germination with 70/30 Preview ───────────────────────────────────

interface GerminationWorkflowInstruction {
  id: string;
  instructionNumber?: string;
  orderItemId?: string;
  cropTypeName?: string;
  variety?: string;
  quantityToSow: number;
  quantitySown?: number;
  unit: number;
  pricingMode?: number;
  trayType?: number;
  trayTypeName?: string;
  scheduledSowingDate: string;
  actualSowingDate?: string;
}

interface RecordGerminationWithPreviewDialogProps {
  instruction: GerminationWorkflowInstruction;
  sowing?: SowingDto;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export const RecordGerminationWithPreviewDialog = ({
  instruction,
  sowing,
  open,
  onOpenChange,
  onSuccess,
}: RecordGerminationWithPreviewDialogProps) => {
  const notify = useNotification();
  const [qty, setQty] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [notes, setNotes] = useState("");
  const [germinationDate, setGerminationDate] = useState(
    new Date().toISOString().split("T")[0]
  );

  // When a specific sowing is provided, use the wave-based API (supports PartiallySown SIs)
  const isWaveMode = !!sowing;

  const qtyNum = parseFloat(qty) || 0;
  const germinationUnitLabel = getUnitLabel(instruction?.unit);
  const maxGerminableQuantity = isWaveMode
    ? sowing.traysRemaining
    : typeof instruction?.quantitySown === "number" && instruction.quantitySown > 0
      ? instruction.quantitySown
      : instruction?.quantityToSow;
  const previewQuery = useGerminationSplitPreview(
    instruction?.id ?? "",
    isWaveMode ? 0 : qtyNum // Skip preview in wave mode (split happens at SI finalization)
  );
  const preview = !isWaveMode ? previewQuery.data : null;

  const recordMutation = useRecordGermination();
  const waveMutation = useRecordGerminationWave();
  const activeMutation = isWaveMode ? waveMutation : recordMutation;
  const greenhousesQuery = useGreenhouses();
  const [selectedGreenhouseIds, setSelectedGreenhouseIds] = useState<string[]>([]);
  const [selectedBayIds, setSelectedBayIds] = useState<string[]>([]);
  const [bedAllocations, setBedAllocations] = useState<Map<string, number>>(new Map());
  const baysQueries = useMultiGreenhouseBays(selectedGreenhouseIds);
  const bedsQueries = useMultiGreenhouseBeds(selectedGreenhouseIds);
  const allBays = baysQueries.flatMap((q) => q.data ?? []);
  const baysLoading = baysQueries.some((q) => q.isLoading);
  const allBedsForSelectedGreenhouses = bedsQueries.flatMap((q) => q.data ?? []);
  const bedsInSelectedBays = allBedsForSelectedGreenhouses.filter(
    (b) => selectedBayIds.includes(b.bayId)
  );
  const [greenhousePopoverOpen, setGreenhousePopoverOpen] = useState(false);
  const [bayPopoverOpen, setBayPopoverOpen] = useState(false);
  const [bedPopoverOpen, setBedPopoverOpen] = useState(false);
  const [greenhouseSearch, setGreenhouseSearch] = useState("");
  const [baySearch, setBaySearch] = useState("");
  const [bedSearch, setBedSearch] = useState("");

  const allocatedTotal = Array.from(bedAllocations.values()).reduce((a, b) => a + b, 0);
  const expectedTotal = Math.floor(qtyNum);
  const hasZeroAllocations = Array.from(bedAllocations.values()).some((c) => c <= 0);
  const waveAllocationsValid =
    !isWaveMode ||
    bedAllocations.size === 0 ||
    (!hasZeroAllocations && allocatedTotal === expectedTotal);
  const waveAllocationDiff = allocatedTotal - expectedTotal;

  // Toggle a greenhouse; removing one clears its bays and bed allocations
  const toggleGreenhouse = (gid: string) => {
    setSelectedGreenhouseIds((prev) => {
      const isRemoving = prev.includes(gid);
      const next = isRemoving ? prev.filter((x) => x !== gid) : [...prev, gid];
      if (isRemoving) {
        const removedBayIds = new Set(allBays.filter((b) => b.greenhouseId === gid).map((b) => b.id));
        setSelectedBayIds((prevBayIds) => prevBayIds.filter((bId) => !removedBayIds.has(bId)));
        setBedAllocations((current) => {
          const updated = new Map(current);
          for (const bedId of Array.from(updated.keys())) {
            const bed = allBedsForSelectedGreenhouses.find((b) => b.id === bedId);
            if (!bed || removedBayIds.has(bed.bayId)) updated.delete(bedId);
          }
          return updated;
        });
      }
      return next;
    });
    setConfirmed(false);
  };
  const toggleBay = (bayId: string) => {
    setSelectedBayIds((prev) => {
      const next = prev.includes(bayId) ? prev.filter((x) => x !== bayId) : [...prev, bayId];
      // Drop bed allocations that no longer belong to a selected bay
      setBedAllocations((current) => {
        const updated = new Map(current);
        for (const bedId of Array.from(updated.keys())) {
          const bed = allBedsForSelectedGreenhouses.find((b) => b.id === bedId);
          if (!bed || !next.includes(bed.bayId)) updated.delete(bedId);
        }
        return updated;
      });
      return next;
    });
    setConfirmed(false);
  };
  const toggleBed = (bedId: string) => {
    setBedAllocations((prev) => {
      const next = new Map(prev);
      if (next.has(bedId)) next.delete(bedId);
      else next.set(bedId, 0);
      return next;
    });
    setConfirmed(false);
  };
  const setBedTrayCount = (bedId: string, value: string) => {
    const n = Math.max(0, parseInt(value || "0", 10) || 0);
    setBedAllocations((prev) => {
      const next = new Map(prev);
      next.set(bedId, n);
      return next;
    });
    setConfirmed(false);
  };

  const handleConfirm = () => {
    if (!qty || qtyNum <= 0) {
      notify.error("Enter a valid quantity germinated");
      return;
    }
    if (
      typeof maxGerminableQuantity === "number" &&
      maxGerminableQuantity > 0 &&
      qtyNum > maxGerminableQuantity
    ) {
      notify.error(
        `Quantity germinated cannot exceed ${maxGerminableQuantity} ${germinationUnitLabel}`
      );
      return;
    }
    if (isWaveMode && selectedGreenhouseIds.length === 0) {
      notify.error("Select at least one greenhouse where this wave is placed");
      return;
    }
    if (isWaveMode && bedAllocations.size > 0) {
      if (Array.from(bedAllocations.values()).some((c) => c <= 0)) {
        notify.error("Each selected bed must have a tray count greater than zero");
        return;
      }
      if (allocatedTotal !== expectedTotal) {
        notify.error(`Per-bed trays must total ${expectedTotal} (currently ${allocatedTotal})`);
        return;
      }
    }
    if (!confirmed) {
      setConfirmed(true);
      return;
    }

    if (isWaveMode) {
      // Wave-based germination recording (per-sowing, supports partial SI)
      waveMutation.mutate(
        {
          sowingId: sowing.id,
          germinationDate: new Date(germinationDate).toISOString(),
          traysGerminated: qtyNum,
          bedAllocations: Array.from(bedAllocations.entries()).map(([bedId, trayCount]) => ({ bedId, trayCount })),
          greenhouseId: bedAllocations.size > 0 ? undefined : selectedGreenhouseIds[0],
        },
        {
          onSuccess: () => {
            notify.success("Germination wave recorded");
            setConfirmed(false);
            setQty("");
            onSuccess();
          },
          onError: (err: unknown) =>
            notify.error(getErrorMessage(err) || "Failed to record germination"),
        }
      );
    } else {
      // Legacy SI-level germination recording
      recordMutation.mutate(
        {
          sowingInstructionId: instruction.id,
          germinationStartedDate: new Date(germinationDate).toISOString(),
          germinationCompletedDate: new Date(germinationDate).toISOString(),
          quantityGerminated: qtyNum,
          notes,
        },
        {
          onSuccess: () => {
            notify.success("Germination recorded — status moved to Ready for Growing");
            setConfirmed(false);
            setQty("");
            onSuccess();
          },
          onError: (err: unknown) =>
            notify.error(getErrorMessage(err) || "Failed to record germination"),
        }
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => {
      setConfirmed(false);
      if (!o) {
        setSelectedGreenhouseIds([]);
        setSelectedBayIds([]);
        setBedAllocations(new Map());
      }
      onOpenChange(o);
    }}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Record Germination</DialogTitle>
          <DialogDescription>
            {instruction?.instructionNumber}
            {isWaveMode
              ? ` — recording for sowing batch (${sowing.traysSown} trays sown on ${new Date(sowing.sowingDate).toLocaleDateString()})`
              : instruction?.orderItemId
                ? " — all germinated stock will be reserved for this customer order."
                : " — the system will calculate the stock/reserve split."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {(() => {
            const trayTypeLabel = instruction?.pricingMode === 2
              ? "Per Seedling/Piece"
              : instruction?.trayTypeName
                || (instruction?.trayType === 210
                  ? "210-Hole Tray"
                  : instruction?.trayType === 442
                    ? "442-Hole Tray"
                    : null);
            const sownDateRaw =
              sowing?.sowingDate
              ?? instruction?.actualSowingDate
              ?? instruction?.scheduledSowingDate;
            const validDate = sownDateRaw && new Date(sownDateRaw).getFullYear() > 1;
            const sownDateLabel = validDate
              ? new Date(sownDateRaw).toLocaleDateString()
              : null;
            const cropTypeLabel = instruction?.cropTypeName;
            const varietyLabel = instruction?.variety;
            if (!trayTypeLabel && !sownDateLabel && !cropTypeLabel && !varietyLabel) return null;
            return (
              <div className="grid grid-cols-2 gap-3 rounded-md border bg-muted/40 px-3 py-2 text-sm">
                {cropTypeLabel && (
                  <div>
                    <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                      Crop Type
                    </p>
                    <p className="font-medium">{cropTypeLabel}</p>
                  </div>
                )}
                {varietyLabel && (
                  <div>
                    <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                      Variety
                    </p>
                    <p className="font-medium">{varietyLabel}</p>
                  </div>
                )}
                {trayTypeLabel && (
                  <div>
                    <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                      Tray Type
                    </p>
                    <p className="font-medium">{trayTypeLabel}</p>
                  </div>
                )}
                {sownDateLabel && (
                  <div>
                    <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                      {sowing?.sowingDate || instruction?.actualSowingDate ? "Date Sown" : "Scheduled Sowing"}
                    </p>
                    <p className="font-medium">{sownDateLabel}</p>
                  </div>
                )}
              </div>
            );
          })()}

          <Field>
            <FieldLabel>Germination Completed Date</FieldLabel>
            <DatePicker
              value={germinationDate}
              onChange={(v) => { setGerminationDate(v); setConfirmed(false); }}
            />
          </Field>

          <Field>
            <FieldLabel>Quantity Germinated *</FieldLabel>
            <Input
              type="number"
              value={qty}
              onChange={(e) => { setQty(e.target.value); setConfirmed(false); }}
              placeholder={`Number of ${germinationUnitLabel} germinated`}
            />
            {typeof maxGerminableQuantity === "number" && maxGerminableQuantity > 0 && (
              <p className="mt-1 text-xs text-muted-foreground">
                Maximum allowed: {maxGerminableQuantity} {germinationUnitLabel} (from {isWaveMode ? "remaining trays in this sowing batch" : instruction?.quantitySown ? "recorded sown quantity" : "sowing instruction quantity"}).
              </p>
            )}
          </Field>

          {isWaveMode && (
            <>
              <Field>
                <FieldLabel>Greenhouse *</FieldLabel>
                <Popover open={greenhousePopoverOpen} onOpenChange={(o) => { setGreenhousePopoverOpen(o); if (!o) setGreenhouseSearch(""); }}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-between font-normal">
                      <span className="truncate">
                        {selectedGreenhouseIds.length === 0
                          ? "Select greenhouses…"
                          : selectedGreenhouseIds.length === 1
                            ? (greenhousesQuery.data?.find((g) => g.id === selectedGreenhouseIds[0])?.greenhouseName ?? "1 greenhouse selected")
                            : `${selectedGreenhouseIds.length} greenhouses selected`}
                      </span>
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                    <div className="flex items-center gap-1.5 border-b px-2 py-1.5">
                      <Search className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                      <input
                        autoFocus
                        placeholder="Search greenhouses…"
                        value={greenhouseSearch}
                        onChange={(e) => setGreenhouseSearch(e.target.value)}
                        className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
                      />
                    </div>
                    <div className="max-h-56 overflow-y-auto p-2 space-y-1">
                      {(greenhousesQuery.data ?? [])
                        .filter((g) => g.greenhouseName.toLowerCase().includes(greenhouseSearch.toLowerCase()))
                        .map((g) => (
                          <label key={g.id} className="flex items-center gap-2 text-sm cursor-pointer rounded px-2 py-1 hover:bg-muted">
                            <input
                              type="checkbox"
                              checked={selectedGreenhouseIds.includes(g.id)}
                              onChange={() => toggleGreenhouse(g.id)}
                            />
                            <span>{g.greenhouseName}</span>
                          </label>
                        ))}
                    </div>
                  </PopoverContent>
                </Popover>
              </Field>

              {selectedGreenhouseIds.length > 0 && (
                <Field>
                  <FieldLabel>Bays</FieldLabel>
                  <Popover open={bayPopoverOpen} onOpenChange={(o) => { setBayPopoverOpen(o); if (!o) setBaySearch(""); }}>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-between font-normal">
                        <span className="truncate">
                          {selectedBayIds.length === 0
                            ? "Select bays…"
                            : `${selectedBayIds.length} bay${selectedBayIds.length !== 1 ? "s" : ""} selected`}
                        </span>
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                      <div className="flex items-center gap-1.5 border-b px-2 py-1.5">
                        <Search className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                        <input
                          autoFocus
                          placeholder="Search bays…"
                          value={baySearch}
                          onChange={(e) => setBaySearch(e.target.value)}
                          className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
                        />
                      </div>
                      <div className="max-h-56 overflow-y-auto p-2 space-y-1">
                        {baysLoading ? (
                          <p className="text-xs text-muted-foreground p-2">Loading bays…</p>
                        ) : allBays.length === 0 ? (
                          <p className="text-xs text-muted-foreground p-2">No bays in selected greenhouse(s).</p>
                        ) : (
                          allBays
                            .filter((bay) => bay.bayName.toLowerCase().includes(baySearch.toLowerCase()))
                            .map((bay) => (
                              <label key={bay.id} className="flex items-center gap-2 text-sm cursor-pointer rounded px-2 py-1 hover:bg-muted">
                                <input
                                  type="checkbox"
                                  checked={selectedBayIds.includes(bay.id)}
                                  onChange={() => toggleBay(bay.id)}
                                />
                                <span>{bay.bayName}</span>
                              </label>
                            ))
                        )}
                      </div>
                    </PopoverContent>
                  </Popover>
                </Field>
              )}

              {selectedBayIds.length > 0 && (
                <Field>
                  <FieldLabel>Beds</FieldLabel>
                  <Popover open={bedPopoverOpen} onOpenChange={(o) => { setBedPopoverOpen(o); if (!o) setBedSearch(""); }}>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-between font-normal">
                        <span className="truncate">
                          {bedAllocations.size === 0
                            ? "Assign trays to beds…"
                            : `${bedAllocations.size} bed${bedAllocations.size !== 1 ? "s" : ""} — ${allocatedTotal}/${expectedTotal}`}
                        </span>
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                      <div className="flex items-center gap-1.5 border-b px-2 py-1.5">
                        <Search className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                        <input
                          autoFocus
                          placeholder="Search beds…"
                          value={bedSearch}
                          onChange={(e) => setBedSearch(e.target.value)}
                          className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
                        />
                      </div>
                      <div className="max-h-64 overflow-y-auto p-2 space-y-1">
                        {bedsQueries.some((q) => q.isLoading) ? (
                          <p className="text-xs text-muted-foreground p-2">Loading beds…</p>
                        ) : bedsInSelectedBays.length === 0 ? (
                          <p className="text-xs text-muted-foreground p-2">No beds in the selected bay(s).</p>
                        ) : (
                          bedsInSelectedBays
                            .filter((bed) =>
                              `${bed.bayName} ${bed.bedNumber}`.toLowerCase().includes(bedSearch.toLowerCase())
                            )
                            .map((bed) => {
                            const isSelected = bedAllocations.has(bed.id);
                            const allocation = bedAllocations.get(bed.id) ?? 0;
                            return (
                              <div
                                key={bed.id}
                                className="flex items-center justify-between gap-2 text-sm rounded px-2 py-1 hover:bg-muted"
                              >
                                <span className="flex items-center gap-2 flex-1">
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() => toggleBed(bed.id)}
                                  />
                                  <span>
                                    <span className="text-muted-foreground">{bed.bayName} /</span> {bed.bedNumber}
                                    {bed.currentOccupancy > 0 && (
                                      <span className="ml-1 text-xs text-muted-foreground">
                                        {bed.currentOccupancy} trays on bed
                                      </span>
                                    )}
                                  </span>
                                </span>
                                {isSelected && (
                                  <Input
                                    type="number"
                                    min="1"
                                    step="1"
                                    placeholder="trays"
                                    value={allocation || ""}
                                    onChange={(e) => setBedTrayCount(bed.id, e.target.value)}
                                    className="h-7 w-20 text-sm"
                                  />
                                )}
                              </div>
                            );
                          })
                        )}
                      </div>
                    </PopoverContent>
                  </Popover>
                  {bedAllocations.size > 0 && (
                    <p className={`mt-1 text-xs break-words ${waveAllocationsValid ? "text-muted-foreground" : "text-destructive"}`}>
                      Allocated {allocatedTotal} / {expectedTotal} trays
                      {waveAllocationDiff > 0 && ` — over by ${waveAllocationDiff}, reduce a bed's count`}
                      {waveAllocationDiff < 0 && ` — need ${-waveAllocationDiff} more tray${-waveAllocationDiff === 1 ? "" : "s"}`}
                    </p>
                  )}
                  <p className="mt-1 text-xs text-muted-foreground">
                    Assign every tray to a specific bed. Per-bed totals must equal the germinated quantity.
                  </p>
                </Field>
              )}
            </>
          )}

          {preview && qtyNum > 0 && (
            <div className="rounded-md border bg-muted/40 p-3 space-y-2">
              {instruction?.orderItemId ? (
                <>
                  <p className="text-sm font-medium flex items-center gap-1">
                    <Info className="h-4 w-4 text-blue-500" />
                    Customer Order — No Split
                  </p>
                  <div className="text-sm">
                    <p className="text-muted-foreground">All germinated stock reserved for order</p>
                    <p className="font-semibold text-green-700 dark:text-green-400">
                      {preview.stockQuantity} {germinationUnitLabel}
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <p className="text-sm font-medium flex items-center gap-1">
                    <Info className="h-4 w-4 text-blue-500" />
                    Stock / Reserve Split Preview
                  </p>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <p className="text-muted-foreground">To greenhouse stock ({Math.round((preview.ratioUsed ?? 0.7) * 100)}%)</p>
                      <p className="font-semibold text-green-700 dark:text-green-400">
                        {preview.stockQuantity} {germinationUnitLabel}
                      </p>
                    </div>
                    <div>
                      <p className="text-muted-foreground">Filling reserve ({Math.round((1 - (preview.ratioUsed ?? 0.7)) * 100)}%)</p>
                      <p className="font-semibold text-amber-700 dark:text-amber-400">
                        {preview.fillingReserve} {germinationUnitLabel}
                      </p>
                    </div>
                  </div>
                </>
              )}
              {preview.warning && (
                <Alert>
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>{preview.warning}</AlertDescription>
                </Alert>
              )}
            </div>
          )}

          {confirmed && (
            <Alert className="border-amber-200 bg-amber-50 dark:bg-amber-950/20">
              <AlertTriangle className="h-4 w-4 text-amber-600" />
              <AlertDescription className="text-amber-800 dark:text-amber-200">
                Confirm you want to record {qtyNum} {germinationUnitLabel} germinated?
                {isWaveMode
                  ? " This records a germination wave for this sowing batch."
                  : " This will set status to Ready for Growing."}
              </AlertDescription>
            </Alert>
          )}

          <Field>
            <FieldLabel>Notes</FieldLabel>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
            />
          </Field>

          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button
              onClick={handleConfirm}
              disabled={activeMutation.isPending || !waveAllocationsValid}
              variant={confirmed ? "destructive" : "default"}
            >
              {activeMutation.isPending
                ? "Recording..."
                : confirmed
                ? "Confirm Record"
                : "Preview & Continue"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ─── Select Germination Batch Dialog (partial-flow router) ──────────────────
// Tray-mode sowing instructions are sown in batches. Germination must be
// recorded per-batch via the wave endpoint, so this dialog lists eligible
// Sowing rows and chains into RecordGerminationWithPreviewDialog with the
// chosen batch. Non-tray (one-shot) instructions should use
// RecordGerminationWithPreviewDialog directly without going through this picker.

interface SelectGerminationBatchDialogProps {
  instruction: GerminationWorkflowInstruction;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export const SelectGerminationBatchDialog = ({
  instruction,
  open,
  onOpenChange,
  onSuccess,
}: SelectGerminationBatchDialogProps) => {
  const sowingsQuery = useSowingsByInstruction(open ? instruction?.id ?? "" : "");
  const [selectedSowing, setSelectedSowing] = useState<SowingDto | null>(null);
  const closeForSowing = useCloseForSowing();
  const { success, error } = useNotification();

  const sowings = sowingsQuery.data ?? [];
  const eligibleSowings = sowings.filter(
    (s) => s.status === "Sown" || s.status === "Germinating"
  );
  const closedSowings = sowings.filter((s) => s.status === "Closed");
  const totalSown = sowings.reduce((sum, s) => sum + s.traysSown, 0);
  const target = instruction?.quantityToSow ?? 0;
  const allClosed = sowings.length > 0 && eligibleSowings.length === 0 && closedSowings.length > 0;
  const isPartial = totalSown < target && totalSown > 0;

  if (selectedSowing) {
    return (
      <RecordGerminationWithPreviewDialog
        instruction={instruction}
        sowing={selectedSowing}
        open={true}
        onOpenChange={(o) => {
          if (!o) setSelectedSowing(null);
        }}
        onSuccess={() => {
          setSelectedSowing(null);
          sowingsQuery.refetch();
          onSuccess();
        }}
      />
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Select Sowing Batch</DialogTitle>
          <DialogDescription>
            {instruction?.instructionNumber} — pick the batch whose germination you want to record. Each batch germinates on its own timeline.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          {sowingsQuery.isLoading ? (
            <p className="text-sm text-gray-500 py-3">Loading sowing batches...</p>
          ) : eligibleSowings.length === 0 ? (
            <p className="text-sm text-gray-500 py-3">
              No sowing batches eligible for germination recording.
            </p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b">
                  <th className="py-2 px-2 font-medium">Sowing Date</th>
                  <th className="py-2 px-2 font-medium">Trays</th>
                  <th className="py-2 px-2 font-medium">Status</th>
                  <th className="py-2 px-2 font-medium">Germinated</th>
                  <th className="py-2 px-2" />
                </tr>
              </thead>
              <tbody>
                {eligibleSowings.map((sowing) => (
                  <tr key={sowing.id} className="border-b last:border-0">
                    <td className="py-2 px-2">
                      {new Date(sowing.sowingDate).toLocaleDateString()}
                    </td>
                    <td className="py-2 px-2">{sowing.traysSown}</td>
                    <td className="py-2 px-2">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          sowing.status === "Germinating"
                            ? "bg-purple-100 text-purple-700"
                            : "bg-blue-100 text-blue-700"
                        }`}
                      >
                        {sowing.status}
                      </span>
                    </td>
                    <td className="py-2 px-2">
                      {sowing.traysGerminated} / {sowing.traysSown}
                    </td>
                    <td className="py-2 px-2 text-right">
                      <Button size="sm" onClick={() => setSelectedSowing(sowing)}>
                        Record Germination
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        {allClosed && isPartial && (
          <Alert className="border-amber-200 bg-amber-50">
            <AlertTriangle className="h-4 w-4 text-amber-600" />
            <AlertDescription className="text-amber-800">
              <p className="font-medium mb-1">Partially sown: {totalSown} / {target} trays</p>
              <p className="text-sm mb-2">
                All batches are closed. Close the instruction to stop further sowing and move {totalSown} trays downstream.
              </p>
              <Button
                size="sm"
                variant="outline"
                className="border-amber-400 text-amber-700 hover:bg-amber-100"
                disabled={closeForSowing.isPending}
                onClick={() => {
                  closeForSowing.mutate(instruction.id, {
                    onSuccess: () => {
                      success("Instruction closed — stock entries moving to Ready for Growing");
                      onOpenChange(false);
                      onSuccess();
                    },
                    onError: (err: unknown) => {
                      error(getErrorMessage(err) || "Failed to close instruction");
                    },
                  });
                }}
              >
                {closeForSowing.isPending ? "Closing..." : `Close instruction (${totalSown} trays)`}
              </Button>
            </AlertDescription>
          </Alert>
        )}
        <div className="flex justify-end pt-3">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
